/* Legend's live candles (mv5-prod/05 §3). The screener publishes, per timeframe, the bar it will store when the
   bucket closes (the forming bar), its last closed bar, and the print watermark wm = {t, k}: the newest print it
   included and how many prints at exactly that ms it included. The chart:
   - candles = closed bars (hydrate) + lastClosed where newer + the forming bar;
   - every snapshot REBUILDS the forming candle: snapshot values + the prints received after its watermark;
   - a print lands in its ET bucket (bins.ts); one older than the forming bucket is late (the screener's stored bar
     wins), one outside 04:00–20:00 ET is ignored (the screener ignores it too);
   - quote mids never make candles.
   Pure (no chart, no DOM): `ChartPane` renders whatever `touched` reports. */
import { bucketStartMs, inSession } from './bins';

export interface WireBar { t: number; o: number; h: number; l: number; c: number; v: number; vw?: number; n?: number }
export interface Wm { t: number; k: number }
/** `asOf` = when the screener built it: frames older than the last applied one are ignored (they can arrive after a
 *  newer hydrate snapshot, buffered while it loaded). */
export interface FormingSnap { wm: Wm | null; forming: WireBar | null; lastClosed: WireBar | null; asOf?: number }
export interface Candle { time: number; open: number; high: number; low: number; close: number; value: number; up: boolean }
export interface Print { ts: number; price: number; size: number }

/** What changed: the candle times touched, ascending, and whether an OLDER bar changed or one was inserted (the
 *  series needs setData; otherwise updating the touched bars in order is enough). */
export interface Touch { times: number[]; reset: boolean }

export const toCandle = (b: WireBar): Candle =>
  ({ time: Math.floor(b.t / 1000), open: b.o, high: b.h, low: b.l, close: b.c, value: b.v, up: b.c >= b.o });

const finite = (b: WireBar | null | undefined): b is WireBar =>
  !!b && [b.t, b.o, b.h, b.l, b.c, b.v].every((x) => typeof x === 'number' && Number.isFinite(x));

const RING_KEEP_MS = 10_000;     // prints this much older than the newest watermark can no longer matter
const RING_CAP = 20_000;

export class LiveCandles {
  /** prints received, oldest first by arrival — re-applied after each snapshot's watermark */
  private ring: Print[] = [];
  private wm: Wm | null = null;
  /** prints at exactly wm.t still to arrive that the snapshot already counted */
  private skipAtWm = 0;
  private lastAsOf = -Infinity;

  /** `candles` is mutated in place (the chart's own array, read by overlays). */
  constructor(readonly candles: Candle[], private readonly tfMin: number) {}

  /** The forming snapshot (hydrate section first, then every WS frame). */
  applySnapshot(s: FormingSnap): Touch {
    if (typeof s.asOf === 'number' && Number.isFinite(s.asOf)) {
      if (s.asOf <= this.lastAsOf) return { times: [], reset: false };
      this.lastAsOf = s.asOf;
    }
    const touched = new Set<number>();
    let reset = false;
    const put = (b: WireBar | null): void => {
      if (!finite(b)) return;
      const c = toCandle(b);
      const arr = this.candles;
      const last = arr[arr.length - 1];
      if (!last || c.time > last.time) { arr.push(c); touched.add(c.time); return; }
      if (c.time === last.time) {
        if (!same(last, c)) { arr[arr.length - 1] = c; touched.add(c.time); }
        return;
      }
      // an older bar: the server's value replaces whatever the browser built (a correction), or fills a hole
      let i = arr.length - 1;
      while (i >= 0 && arr[i]!.time > c.time) i--;
      if (i >= 0 && arr[i]!.time === c.time) { if (!same(arr[i]!, c)) { arr[i] = c; reset = true; touched.add(c.time); } }
      else { arr.splice(i + 1, 0, c); reset = true; touched.add(c.time); }
    };
    put(s.lastClosed);
    // The forming candle is rebuilt only from a snapshot at least as new as it: then it holds exactly the snapshot's
    // values, and the prints after THAT snapshot's watermark go back on top. A staler frame changes nothing (its
    // prints are already in the newer candle; the server's lastClosed corrects the older bar after the boundary).
    const lastT = this.candles.length ? this.candles[this.candles.length - 1]!.time : -Infinity;
    if (finite(s.forming) && toCandle(s.forming).time >= lastT && !(s.wm && Number.isFinite(s.wm.t))) {
      // no watermark: the screener has accepted no print for the symbol since it (re)started — its forming bar is the
      // restored partial bucket, and nothing in the ring is in it; prints from here on are new
      put(s.forming);
      this.wm = null;
      this.skipAtWm = 0;
    } else if (finite(s.forming) && toCandle(s.forming).time >= lastT && s.wm && Number.isFinite(s.wm.t)) {
      put(s.forming);
      this.wm = { t: s.wm.t, k: s.wm.k };
      let atWm = 0;
      for (const p of this.ring) {
        if (p.ts < this.wm.t) continue;
        if (p.ts === this.wm.t && ++atWm <= this.wm.k) continue;
        this.place(p, touched);
      }
      this.skipAtWm = Math.max(0, this.wm.k - atWm);
      const keepFrom = this.wm.t - RING_KEEP_MS;
      if (this.ring.length && this.ring[0]!.ts < keepFrom) this.ring = this.ring.filter((p) => p.ts >= keepFrom);
    }
    return { times: [...touched].sort((a, b) => a - b), reset };
  }

  /** One trade print. */
  applyPrint(p: Print): Touch {
    const none = { times: [], reset: false };
    // the screener's own rule (BarSessionRunner.onPrint): p > 0, s > 0, inside 04:00–20:00 ET — anything else is in no bar
    if (!Number.isFinite(p.ts) || !(p.price > 0) || !(p.size > 0) || !inSession(p.ts)) return none;
    this.ring.push(p);
    if (this.ring.length > RING_CAP) this.ring.splice(0, this.ring.length - RING_CAP);
    if (this.wm) {
      if (p.ts < this.wm.t) return none;                           // the snapshot holds it (or it is late)
      if (p.ts === this.wm.t && this.skipAtWm > 0) { this.skipAtWm--; return none; }
    }
    const touched = new Set<number>();
    this.place(p, touched);
    return { times: [...touched], reset: false };
  }

  private place(p: Print, touched: Set<number>): void {
    const t = Math.floor(bucketStartMs(p.ts, this.tfMin) / 1000);
    const arr = this.candles;
    const last = arr[arr.length - 1];
    const size = p.size;
    if (!last || t > last.time) {
      arr.push({ time: t, open: p.price, high: p.price, low: p.price, close: p.price, value: size, up: true });
    } else if (t === last.time) {
      last.close = p.price;
      if (p.price > last.high) last.high = p.price;
      if (p.price < last.low) last.low = p.price;
      last.value += size;
      last.up = last.close >= last.open;
    } else {
      return;                                                      // late: older than the forming bucket
    }
    touched.add(t);
  }
}

function same(a: Candle, b: Candle): boolean {
  return a.open === b.open && a.high === b.high && a.low === b.low && a.close === b.close && a.value === b.value;
}
